import { imageElementToBlob } from '@/shared/helpers/generationHistory';

export const GH_DRAG_MIME_IMAGE = 'application/x-pdrai-gh-image';
export const GH_DRAG_MIME_TEXT = 'application/x-pdrai-gh-text';

export const GH_DRAG_SOURCE = {
  DESIGN: 'design',
  CONCEPT: 'concept',
  NAMING: 'naming',
};

const DESIGN_GENERATION_ROUTE_NAMES = ['StableDiffusionCreate'];
const GH_DRAG_PREVIEW_MAX_PX = 120;

/** In-page drag session: holds local element / dataUrl (not serializable via dataTransfer). */
let activeGhImageDragSession = null;

function removeGhDragPreviewElement(previewEl) {
  if (previewEl && previewEl.parentNode) {
    previewEl.parentNode.removeChild(previewEl);
  }
}

/**
 * Custom drag ghost with natural aspect ratio — avoids square layout box + overflow clipping.
 * @param {DataTransfer} dataTransfer
 * @param {HTMLImageElement} img
 * @returns {HTMLImageElement|null}
 */
export function applyGhDragImagePreview(dataTransfer, img) {
  if (!dataTransfer || !img || !(typeof HTMLImageElement !== 'undefined' && img instanceof HTMLImageElement)) {
    return null;
  }

  const naturalW = img.naturalWidth;
  const naturalH = img.naturalHeight;
  let previewW = GH_DRAG_PREVIEW_MAX_PX;
  let previewH = GH_DRAG_PREVIEW_MAX_PX;

  if (naturalW > 0 && naturalH > 0) {
    const ratio = naturalW / naturalH;
    if (ratio >= 1) {
      previewH = Math.max(1, Math.round(previewW / ratio));
    } else {
      previewW = Math.max(1, Math.round(previewH * ratio));
    }
  } else {
    const rect = img.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      previewW = Math.min(GH_DRAG_PREVIEW_MAX_PX, Math.round(rect.width));
      previewH = Math.min(GH_DRAG_PREVIEW_MAX_PX, Math.round(rect.height));
    }
  }

  const preview = document.createElement('img');
  preview.src = img.currentSrc || img.src;
  preview.alt = '';
  preview.width = previewW;
  preview.height = previewH;
  preview.style.cssText = [
    'position:fixed',
    'top:-9999px',
    'left:-9999px',
    'width:' + previewW + 'px',
    'height:' + previewH + 'px',
    'object-fit:contain',
    'pointer-events:none',
    'opacity:1',
    'border-radius:4px',
    'box-shadow:0 4px 12px rgba(0,0,0,0.18)',
    'background:#fff',
  ].join(';');
  document.body.appendChild(preview);

  dataTransfer.setDragImage(
    preview,
    Math.round(previewW / 2),
    Math.round(previewH / 2),
  );
  return preview;
}

export function isDesignGenerationRoute(route) {
  if (!route || !route.name) {
    return false;
  }
  return DESIGN_GENERATION_ROUTE_NAMES.indexOf(route.name) !== -1;
}

export function buildGhImagePayload({ url, itemId, sourceType, dataUrl, itemType, posterIndex }) {
  return {
    kind: 'image',
    url: url || '',
    itemId: itemId == null ? null : itemId,
    itemType: itemType == null ? null : itemType,
    posterIndex: posterIndex == null ? 0 : posterIndex,
    sourceType: sourceType || '',
    text: '',
    dataUrl: dataUrl || '',
  };
}

export function buildGhTextPayload({ text, itemId, sourceType }) {
  return {
    kind: 'text',
    url: '',
    itemId: itemId == null ? null : itemId,
    sourceType: sourceType || '',
    text: text || '',
  };
}

function safeParse(raw) {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function getDataTransferTypes(dataTransfer) {
  if (!dataTransfer || !dataTransfer.types) {
    return [];
  }
  return Array.from(dataTransfer.types);
}

/**
 * Check MIME presence without getData (usable during dragover).
 * @param {DataTransfer} dataTransfer
 * @param {string} mime
 * @returns {boolean}
 */
export function dataTransferHasGhMime(dataTransfer, mime) {
  return getDataTransferTypes(dataTransfer).indexOf(mime) !== -1;
}

/**
 * @param {DataTransfer} dataTransfer
 * @returns {{ kind: string, url: string, text: string, itemId: *, sourceType: string, dataUrl?: string } | null}
 */
export function readGhDragPayload(dataTransfer) {
  if (!dataTransfer) {
    return null;
  }
  const types = getDataTransferTypes(dataTransfer);
  if (types.indexOf(GH_DRAG_MIME_IMAGE) !== -1) {
    const parsed = safeParse(dataTransfer.getData(GH_DRAG_MIME_IMAGE));
    if (parsed && parsed.kind === 'image') {
      return parsed;
    }
  }
  if (types.indexOf(GH_DRAG_MIME_TEXT) !== -1) {
    const parsed = safeParse(dataTransfer.getData(GH_DRAG_MIME_TEXT));
    if (parsed && parsed.kind === 'text') {
      return parsed;
    }
  }
  return null;
}

/**
 * Sync read of already-displayed <img> into a data URL (local view).
 * Returns '' when canvas is tainted / image not ready.
 */
export function tryImageElementToDataUrl(img) {
  if (!img || !(typeof HTMLImageElement !== 'undefined' && img instanceof HTMLImageElement)) {
    return '';
  }
  if (!img.naturalWidth || !img.naturalHeight) {
    return '';
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch (e) {
    return '';
  }
}

export function clearGhImageDragSession() {
  if (activeGhImageDragSession && activeGhImageDragSession.previewEl) {
    removeGhDragPreviewElement(activeGhImageDragSession.previewEl);
  }
  activeGhImageDragSession = null;
}

export function getGhImageDragSession() {
  return activeGhImageDragSession;
}

export function setGhImageDragData(dataTransfer, payload, options = {}) {
  if (!dataTransfer || !payload) {
    return;
  }
  const element = options.element || null;
  // Prefer local data URL from the displayed element (avoids CORS / wrong API id).
  let localDataUrl = payload.dataUrl || '';
  if (!localDataUrl && element) {
    localDataUrl = tryImageElementToDataUrl(element);
  }
  let previewEl = null;
  if (element && typeof HTMLImageElement !== 'undefined' && element instanceof HTMLImageElement) {
    previewEl = applyGhDragImagePreview(dataTransfer, element);
  }
  // Keep local bytes in-memory only — dataTransfer JSON size limits are too small for images.
  activeGhImageDragSession = {
    element,
    previewEl,
    dataUrl: localDataUrl || '',
    url: payload.url || '',
    itemId: payload.itemId,
    sourceType: payload.sourceType || '',
  };
  const transferPayload = {
    ...payload,
    // marker only; actual bytes live in activeGhImageDragSession
    dataUrl: localDataUrl ? 'local' : '',
  };
  dataTransfer.effectAllowed = 'copy';
  dataTransfer.setData(GH_DRAG_MIME_IMAGE, JSON.stringify(transferPayload));
}

export function setGhTextDragData(dataTransfer, payload) {
  if (!dataTransfer || !payload) {
    return;
  }
  dataTransfer.effectAllowed = 'copy';
  dataTransfer.setData(GH_DRAG_MIME_TEXT, JSON.stringify(payload));
  dataTransfer.setData('text/plain', payload.text || '');
}

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('blob_to_data_url_failed'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Resolve GH image payload to a data URL for sdMixins.showImage.
 * Priority: local element/dataUrl → URL fetch → history image API (design + concept).
 */
export async function resolveGhDragImageToDataUrl(payload, fetchBlobFn) {
  if (!payload || payload.kind !== 'image') {
    throw new Error('gh_image_invalid_payload');
  }

  // Snapshot session synchronously — dragend may clear it before async work finishes.
  const session = activeGhImageDragSession;
  const localDataUrl = (session && session.dataUrl) || '';
  const localElement = session && session.element ? session.element : null;

  // 1) Local view from dragged <img>
  if (localDataUrl && localDataUrl !== 'local') {
    return localDataUrl;
  }
  if (localElement) {
    try {
      const blobFromElement = await imageElementToBlob(localElement);
      return blobToDataUrl(blobFromElement);
    } catch (e) {
      // tainted canvas / load error — fallback below
    }
  }

  // 2) Authenticated history image API (design result or concept poster)
  let blob = null;
  if (payload.itemId != null && typeof fetchBlobFn === 'function') {
    try {
      blob = await fetchBlobFn(payload.itemId, {
        itemType: payload.itemType,
        index: payload.posterIndex,
      });
    } catch (e) {
      blob = null;
    }
  }
  if (blob) {
    return blobToDataUrl(blob);
  }

  // 3) Direct URL fetch
  if (payload.url) {
    try {
      const response = await fetch(payload.url, { mode: 'cors' });
      if (response.ok) {
        blob = await response.blob();
      }
    } catch (e) {
      blob = null;
    }
  }
  if (blob) {
    return blobToDataUrl(blob);
  }

  throw new Error('gh_image_resolve_failed');
}
